/**
 * Environment configuration for frontend applications
 * Single source of truth for all frontend environment variables
 *
 * This is a standard Angular environment pattern - no Vite env loading needed.
 * For production, these values would be replaced at build time or via environment-specific files.
 */

export interface Environment {
	/**
	 * Backend API server URL
	 * Development: http://localhost:5510
	 * Production: https://api.yourdomain.com
	 */
	serverUrl: string;

	/**
	 * Main Dash app URL
	 * Development: http://localhost:5500
	 * Production: https://yourdomain.com
	 */
	dashUrl: string;

	/**
	 * Admin app URL
	 * Development: http://localhost:5501
	 * Production: https://admin.yourdomain.com
	 */
	adminUrl: string;

	/**
	 * Image CDN URL for R2 served images
	 * Development: http://localhost:5510 (served by backend)
	 * Production: https://image.magpie.team
	 */
	imageCdnUrl: string;

	/**
	 * Cloudflare account ID for image uploads
	 */
	cloudflareAccountId: string;

	/**
	 * Cloudflare direct upload token for image uploads
	 */
	cloudflareImagesToken: string;

	/**
	 * PartyKit server URL for real-time game sessions
	 * Development: http://127.0.0.1:5511
	 * Production: https://dash-party.your-account.partykit.dev
	 */
	partyUrl: string;

	/**
	 * PostHog analytics project API key
	 */
	posthogKey: string;

	/**
	 * PostHog analytics host URL
	 */
	posthogHost: string;

	/**
	 * OneSignal App ID for push notifications
	 */
	oneSignalAppId: string;

	/**
	 * Sentry DSN for error tracking
	 */
	sentryDsn: string;

	/**
	 * Sentry environment name (development, production, etc.)
	 */
	sentryEnvironment: string;
}

/**
 * Development environment configuration
 * Update these values for your local development setup
 */
// Dev-machine LAN IP — used when running the app on a physical device
// (iOS/Android) where "localhost" means the phone, not your Mac.
// Update this to match your Mac's LAN IP (run `ipconfig getifaddr en0`).
// Browser-based dev still works against localhost because Capacitor isn't in play.
const DEV_LAN_IP = "192.168.68.65";
const isNativeApp =
	typeof window !== "undefined" &&
	(
		window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }
	).Capacitor?.isNativePlatform?.() === true;

const devHost = isNativeApp ? DEV_LAN_IP : "localhost";

// Hostname-based routing: when the app is loaded from `dash.<domain>` (e.g.
// dash.carolinecowley.uk via Cloudflare Tunnel, or dash.localhost via
// portless), infer sibling subdomains for edge/party so the client doesn't
// try to call its own localhost.
const tunnelDomain =
	typeof window !== "undefined" &&
	window.location.hostname.startsWith("dash.")
		? window.location.hostname.slice("dash.".length)
		: null;

const tunnelProtocol =
	typeof window !== "undefined" ? window.location.protocol : "http:";

const serverUrl = tunnelDomain
	? `${tunnelProtocol}//edge.${tunnelDomain}`
	: `http://${devHost}:5510`;
const dashUrl = tunnelDomain
	? `${tunnelProtocol}//dash.${tunnelDomain}`
	: `http://${devHost}:5500`;
const adminUrl = tunnelDomain
	? `${tunnelProtocol}//dash-admin.${tunnelDomain}`
	: `http://${devHost}:5501`;
const partyUrl = tunnelDomain
	? `${tunnelProtocol}//party.${tunnelDomain}`
	: `http://${devHost}:5511`;

export const environment: Environment = {
	serverUrl,
	dashUrl,
	adminUrl,
	imageCdnUrl: "https://image.magpie.team",
	cloudflareAccountId: "your-account-id-here",
	cloudflareImagesToken: "your-direct-upload-token-here",
	partyUrl,
	// PostHog project key is loaded from VITE_POSTHOG_KEY in apps/dash/.env.local
	// Set via: ./setup-secrets.sh --posthog
	// (Publishable client-side key — safe to ship in bundles, but kept in env so
	// Droid-Shield doesn't flag it on commit. See `dash-secrets` skill.)
	posthogKey: getViteEnv("VITE_POSTHOG_KEY") ?? "",
	posthogHost: "https://eu.i.posthog.com",
	oneSignalAppId: "REPLACE_WITH_ONESIGNAL_APP_ID",
	// Sentry DSN is loaded from VITE_DASH_SENTRY_DSN in apps/dash/.env.local
	// Set via: ./setup-secrets.sh --dash
	sentryDsn: getViteEnv("VITE_DASH_SENTRY_DSN") ?? "",
	sentryEnvironment:
		getViteEnv("VITE_DASH_SENTRY_ENVIRONMENT") ??
		(getViteEnv("PROD") === "true" ? "production" : "development"),
};

/**
 * Read a Vite env var at build time.
 * Returns undefined if not set or if import.meta.env is unavailable (e.g. Node context).
 */
function getViteEnv(key: string): string | undefined {
	try {
		const env = (
			import.meta as unknown as { env?: Record<string, string | boolean> }
		).env;
		const value = env?.[key];
		return value === undefined ? undefined : String(value);
	} catch {
		return undefined;
	}
}
